import { useState, useEffect } from "react";
import { CalendarClock, Video, MapPin, Banknote, Plus, Trash2, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtDT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const MeetingsPanel = ({ clientId = null, isAdmin = false }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { title: "", instructions: "", zoom_link: "", location_name: "", location_address: "", start_time: "", end_time: "", fees: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      const { data } = await api.get("/meetings", { params });
      setMeetings(data);
    } catch { toast.error("Failed to load meetings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/meetings", { ...form, client_id: clientId });
      toast.success("Meeting scheduled");
      setForm(empty); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/meetings/${id}`); toast.success("Meeting removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const inputCls = "w-full bg-[#0d0d0d] border border-white/12 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-zinc-400 text-sm"><CalendarClock size={16} /> {meetings.length} meeting{meetings.length !== 1 ? "s" : ""}</div>
        {isAdmin && (
          <button onClick={() => setShowForm(!showForm)} data-testid="add-meeting-btn" className="btn-metal px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2">
            {showForm ? <X size={15} /> : <Plus size={15} />} {showForm ? "Close" : "Schedule"}
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <form onSubmit={create} data-testid="meeting-form" className="rounded-xl border border-white/10 bg-[#0b0b0b] p-6 mb-8 space-y-4">
          <input required className={inputCls} placeholder="Meeting title (e.g. Valuation review)" value={form.title} onChange={set("title")} data-testid="meeting-title" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-zinc-500">Start</label><input required type="datetime-local" className={`${inputCls} mt-1`} value={form.start_time} onChange={set("start_time")} data-testid="meeting-start" /></div>
            <div><label className="text-xs text-zinc-500">End</label><input required type="datetime-local" className={`${inputCls} mt-1`} value={form.end_time} onChange={set("end_time")} data-testid="meeting-end" /></div>
          </div>
          <input className={inputCls} placeholder="Zoom / video call link (optional)" value={form.zoom_link} onChange={set("zoom_link")} data-testid="meeting-zoom" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={inputCls} placeholder="Location name (optional)" value={form.location_name} onChange={set("location_name")} data-testid="meeting-location-name" />
            <input className={inputCls} placeholder="Agreed fees (e.g. £2,500 + VAT)" value={form.fees} onChange={set("fees")} data-testid="meeting-fees" />
          </div>
          <input className={inputCls} placeholder="Full address for map (optional)" value={form.location_address} onChange={set("location_address")} data-testid="meeting-address" />
          <textarea rows={3} className={inputCls} placeholder="Instructions for the meeting (optional)" value={form.instructions} onChange={set("instructions")} data-testid="meeting-instructions" />
          <button type="submit" disabled={saving} className="btn-metal px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60" data-testid="meeting-save">{saving ? "Saving…" : "Save meeting"}</button>
        </form>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/12 py-16 text-center text-zinc-500" data-testid="meetings-empty">
          <CalendarClock size={28} strokeWidth={1} className="mx-auto mb-3 opacity-60" />
          No meetings scheduled.
        </div>
      ) : (
        <div className="space-y-5" data-testid="meetings-list">
          {meetings.map((m) => (
            <div key={m.id} className="rounded-xl border border-white/10 bg-[#0b0b0b] overflow-hidden" data-testid={`meeting-card-${m.id}`}>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-dash text-xl text-white">{m.title}</h3>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-zinc-400">
                      <span className="flex items-center gap-1.5"><Clock size={14} /> {fmtDT(m.start_time)}</span>
                      {m.end_time && <span className="text-zinc-500">→ {fmtDT(m.end_time)}</span>}
                    </div>
                  </div>
                  {isAdmin && <button onClick={() => remove(m.id)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`delete-meeting-${m.id}`}><Trash2 size={16} /></button>}
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {m.fees && <div className="flex items-center gap-2 text-sm text-zinc-300"><Banknote size={15} className="text-zinc-500" /> {m.fees}</div>}
                  {m.location_name && <div className="flex items-center gap-2 text-sm text-zinc-300"><MapPin size={15} className="text-zinc-500" /> {m.location_name}</div>}
                </div>

                {m.instructions && <p className="text-zinc-400 text-sm mt-4 leading-relaxed whitespace-pre-line">{m.instructions}</p>}

                {m.zoom_link && (
                  <a href={m.zoom_link} target="_blank" rel="noreferrer" data-testid={`meeting-join-${m.id}`}
                    className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-lg border border-white/20 text-zinc-200 hover:border-white/50 hover:text-white transition-all text-sm">
                    <Video size={15} /> Join video call
                  </a>
                )}
              </div>
              {m.location_address && (
                <iframe title={`map-${m.id}`} data-testid={`meeting-map-${m.id}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(m.location_address)}&output=embed`}
                  className="w-full h-56 border-t border-white/10 grayscale-[0.3]" loading="lazy" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
